/**
 * Unified Tracking Hook
 * Replaces useAffiliateTracking, useContactTracking, and provides centralized tracking
 */

import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  trackingManager,
  trackPageView,
  trackEvent,
  trackClick,
  trackContactClick,
  trackSocialShare,
} from '@/utils/trackingManager';
import { getDeviceHash, getSessionId } from '@/utils/deviceDetection';

/**
 * Main unified tracking hook
 * Initializes tracking and provides all tracking methods
 */
export const useTracking = () => {
  // Initialize tracking on mount
  useEffect(() => {
    trackingManager.init();

    // Track affiliate clicks
    const trackAffiliateFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');

      if (!refCode) return;

      // Check if already tracked this session
      const sessionKey = `affiliate_click_${refCode}_${getSessionId()}`;
      if (sessionStorage.getItem(sessionKey)) {
        // Still store data for conversion tracking
        await storeAffiliateData(refCode);
        return;
      }

      try {
        // Find affiliate by code
        const { data: affiliates } = await supabase
          .from('affiliate_profiles')
          .select('user_id, enlace, cookie_window')
          .ilike('enlace', `%${refCode}%`);

        if (!affiliates || affiliates.length === 0) return;

        const affiliate = affiliates[0];

        // Track the click
        await trackingManager.trackAffiliateClick(refCode, affiliate.user_id);

        // Store session data
        sessionStorage.setItem(sessionKey, 'true');
        sessionStorage.setItem('affiliate_ref', refCode);
        sessionStorage.setItem('affiliate_user_id', affiliate.user_id);
        sessionStorage.setItem('device_hash', getDeviceHash());

        // Store localStorage data with expiration
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + affiliate.cookie_window);

        localStorage.setItem('affiliate_ref', refCode);
        localStorage.setItem('affiliate_user_id', affiliate.user_id);
        localStorage.setItem('affiliate_expiration', expirationDate.toISOString());

        // Affiliate click tracked successfully
      } catch (error) {
        console.error('[Tracking] Error tracking affiliate:', error);
      }
    };

    trackAffiliateFromUrl();

    // Handle route changes
    const handleRouteChange = () => {
      trackPageView();
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  // Helper to store affiliate data without full tracking
  const storeAffiliateData = async (refCode: string) => {
    try {
      sessionStorage.setItem('device_hash', getDeviceHash());
      sessionStorage.setItem('affiliate_ref', refCode);

      const { data: affiliates } = await supabase
        .from('affiliate_profiles')
        .select('user_id, cookie_window')
        .ilike('enlace', `%${refCode}%`);

      if (affiliates && affiliates.length > 0) {
        const affiliate = affiliates[0];
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + affiliate.cookie_window);

        sessionStorage.setItem('affiliate_user_id', affiliate.user_id);
        localStorage.setItem('affiliate_ref', refCode);
        localStorage.setItem('affiliate_user_id', affiliate.user_id);
        localStorage.setItem('affiliate_expiration', expirationDate.toISOString());
      }
    } catch (error) {
      console.error('[Tracking] Error storing affiliate data:', error);
    }
  };

  // Get affiliate data for conversions
  const getAffiliateData = useCallback(() => {
    const refCode = localStorage.getItem('affiliate_ref');
    const affiliateUserId = localStorage.getItem('affiliate_user_id');
    const expiration = localStorage.getItem('affiliate_expiration');

    if (!refCode || !affiliateUserId || !expiration) {
      return null;
    }

    // Check if expired
    if (new Date(expiration) < new Date()) {
      localStorage.removeItem('affiliate_ref');
      localStorage.removeItem('affiliate_user_id');
      localStorage.removeItem('affiliate_expiration');
      return null;
    }

    return {
      refCode,
      affiliateUserId,
      expiration: new Date(expiration),
    };
  }, []);

  return {
    // Page tracking
    trackPageView,

    // Event tracking
    trackEvent,
    trackClick,

    // Contact tracking (for listings)
    trackContactClick,
    trackSocialShare,

    // Affiliate data
    getAffiliateData,

    // Session info
    getSessionId,
    getDeviceHash,

    // Force flush (for critical events)
    forceFlush: () => trackingManager.forceFlush(),
  };
};

/**
 * Hook for contact tracking (backward compatibility)
 * @deprecated Use useTracking instead
 */
export const useContactTracking = () => {
  const { trackContactClick, trackSocialShare } = useTracking();

  return {
    trackContactClick,
    trackSocialShare,
  };
};

/**
 * Hook for affiliate tracking (backward compatibility)
 * @deprecated Use useTracking instead
 */
export const useAffiliateTracking = () => {
  const { getAffiliateData } = useTracking();

  // The actual tracking is done in useTracking's useEffect
  return {
    getAffiliateData,
  };
};

export default useTracking;
